import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';

const VendorQuoteForm = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [expired, setExpired] = useState(false);
  const [quotes, setQuotes] = useState({});

  const loadFormData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/rfq-workflow/vendor-form/${token}`);
      const data = response.data;

      if (!data.success) {
        if (data.already_submitted) {
          setAlreadySubmitted(true);
          return;
        }
        if (data.expired) {
          setExpired(true);
          return;
        }
        setError(data.message || 'Failed to load form');
        return;
      }

      setFormData(data);

      // Initialize quotes state for each item
      const initialQuotes = {};
      (data.items || []).forEach(item => {
        initialQuotes[item.id] = {
          item_id: item.id,
          unit_price: '',
          quantity_offered: item.quantity || '',
          total_price: '',
          delivery_days: '',
          estimated_ship_date: '',
          payment_terms: '',
          notes: ''
        };
      });
      setQuotes(initialQuotes);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This quote form link is invalid or has expired.');
      } else {
        setError('Failed to load the quote form. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadFormData();
    }
  }, [token, loadFormData]);

  const handleQuoteChange = (itemId, field, value) => {
    setQuotes(prev => {
      const updated = {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          [field]: value
        }
      };

      // Auto-calculate total price
      if (field === 'unit_price' || field === 'quantity_offered') {
        const unitPrice = field === 'unit_price' ? value : updated[itemId].unit_price;
        const qty = field === 'quantity_offered' ? value : updated[itemId].quantity_offered;
        if (unitPrice && qty) {
          updated[itemId].total_price = (parseFloat(unitPrice) * parseFloat(qty)).toFixed(2);
        }
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate at least one item has a unit price
    const quotesArray = Object.values(quotes).filter(q => q.unit_price);
    if (quotesArray.length === 0) {
      setError('Please enter a unit price for at least one item.');
      return;
    }

    // Convert string values to numbers
    const processedQuotes = quotesArray.map(q => ({
      ...q,
      unit_price: q.unit_price ? parseFloat(q.unit_price) : null,
      quantity_offered: q.quantity_offered ? parseFloat(q.quantity_offered) : null,
      total_price: q.total_price ? parseFloat(q.total_price) : null,
      delivery_days: q.delivery_days ? parseInt(q.delivery_days) : null
    }));

    try {
      setSubmitting(true);
      setError(null);
      await axios.post(`${API_BASE_URL}/api/rfq-workflow/vendor-form/${token}/submit`, {
        quotes: processedQuotes
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('already been submitted')) {
        setAlreadySubmitted(true);
      } else if (detail.includes('expired') || detail.includes('bidding period')) {
        setExpired(true);
      } else if (detail) {
        setError(detail);
      } else {
        setError('Failed to submit the quote. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.card}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading quote form...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !formData) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.card}>
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>!</div>
            <h2 style={styles.errorTitle}>Unable to Load Form</h2>
            <p style={styles.errorText}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Already submitted
  if (alreadySubmitted) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.card}>
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>&#10003;</div>
            <h2 style={styles.successTitle}>Already Submitted</h2>
            <p style={styles.successText}>This quote form has already been submitted. Thank you!</p>
          </div>
        </div>
      </div>
    );
  }

  // Expired
  if (expired) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.card}>
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>!</div>
            <h2 style={styles.errorTitle}>Link Expired</h2>
            <p style={styles.errorText}>This quote form link has expired. Please contact the buyer for a new link.</p>
          </div>
        </div>
      </div>
    );
  }

  // Submitted success
  if (submitted) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.card}>
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>&#10003;</div>
            <h2 style={styles.successTitle}>Quote Submitted Successfully!</h2>
            <p style={styles.successText}>
              Thank you, {formData?.vendor_name}! Your quote for RFQ #{formData?.rfq_number} has been received.
              Our team will review your submission and get back to you.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div style={styles.pageContainer}>
      <div style={styles.formCard}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>Vendor Quote Submission</h1>
          <p style={styles.headerSubtitle}>RFQ #{formData?.rfq_number} - {formData?.rfq_title}</p>
        </div>

        {/* Vendor Info */}
        <div style={styles.infoBar}>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Vendor:</span>
            <span style={styles.infoValue}>{formData?.vendor_name}</span>
          </div>
          {formData?.bid_end_date && (
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Bid Deadline:</span>
              <span style={styles.infoValue}>{formData.bid_end_date}</span>
            </div>
          )}
        </div>

        {error && (
          <div style={styles.errorBanner}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Items */}
          {(formData?.items || []).map((item, index) => (
            <div key={item.id} style={styles.itemCard}>
              <div style={styles.itemHeader}>
                <span style={styles.itemNumber}>Item {index + 1}</span>
                <span style={styles.itemName}>{item.item_name}</span>
              </div>
              <div style={styles.itemDetails}>
                <span style={styles.itemDetail}>Requested Qty: <strong>{item.quantity} {item.unit || 'units'}</strong></span>
                {item.specifications && (
                  <span style={styles.itemDetail}>Specs: {item.specifications}</span>
                )}
              </div>

              <div style={styles.fieldsGrid}>
                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>Unit Price (USD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    style={styles.input}
                    placeholder="0.00"
                    value={quotes[item.id]?.unit_price || ''}
                    onChange={(e) => handleQuoteChange(item.id, 'unit_price', e.target.value)}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>Quantity Offered</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    style={styles.input}
                    placeholder={item.quantity || '0'}
                    value={quotes[item.id]?.quantity_offered || ''}
                    onChange={(e) => handleQuoteChange(item.id, 'quantity_offered', e.target.value)}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>Total Price (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    style={{...styles.input, backgroundColor: '#f3f4f6'}}
                    placeholder="Auto-calculated"
                    value={quotes[item.id]?.total_price || ''}
                    onChange={(e) => handleQuoteChange(item.id, 'total_price', e.target.value)}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>Delivery Days / Lead Time</label>
                  <input
                    type="number"
                    min="0"
                    style={styles.input}
                    placeholder="e.g. 14"
                    value={quotes[item.id]?.delivery_days || ''}
                    onChange={(e) => handleQuoteChange(item.id, 'delivery_days', e.target.value)}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>Estimated Ship Date</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={quotes[item.id]?.estimated_ship_date || ''}
                    onChange={(e) => handleQuoteChange(item.id, 'estimated_ship_date', e.target.value)}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>Payment Terms</label>
                  <select
                    style={styles.input}
                    value={quotes[item.id]?.payment_terms || ''}
                    onChange={(e) => handleQuoteChange(item.id, 'payment_terms', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="2/10 Net 30">2/10 Net 30</option>
                    <option value="COD">COD</option>
                  </select>
                </div>
              </div>

              <div style={styles.notesGroup}>
                <label style={styles.fieldLabel}>Notes / Comments</label>
                <textarea
                  style={styles.textarea}
                  rows={2}
                  placeholder="Any additional notes about this item..."
                  value={quotes[item.id]?.notes || ''}
                  onChange={(e) => handleQuoteChange(item.id, 'notes', e.target.value)}
                />
              </div>
            </div>
          ))}

          {/* Submit */}
          <div style={styles.submitSection}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                ...styles.submitButton,
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Quote'}
            </button>
            <p style={styles.submitNote}>
              Fields marked with * are required. You only need to fill items you wish to quote.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#f0f2f5',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '24px 16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '48px',
    maxWidth: '500px',
    width: '100%',
    marginTop: '80px',
    textAlign: 'center',
  },
  formCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    maxWidth: '800px',
    width: '100%',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
    padding: '28px 32px',
    color: '#ffffff',
  },
  headerTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '700',
  },
  headerSubtitle: {
    margin: '6px 0 0 0',
    fontSize: '14px',
    opacity: 0.9,
  },
  infoBar: {
    display: 'flex',
    gap: '24px',
    padding: '14px 32px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    flexWrap: 'wrap',
  },
  infoItem: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    fontSize: '13px',
  },
  infoLabel: {
    color: '#64748b',
    fontWeight: '500',
  },
  infoValue: {
    color: '#1e293b',
    fontWeight: '600',
  },
  errorBanner: {
    margin: '16px 32px 0',
    padding: '10px 16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '13px',
    border: '1px solid #fecaca',
  },
  itemCard: {
    margin: '20px 32px 0',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  itemNumber: {
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  itemName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
  },
  itemDetails: {
    display: 'flex',
    gap: '16px',
    padding: '8px 16px',
    backgroundColor: '#fefce8',
    borderBottom: '1px solid #e2e8f0',
    flexWrap: 'wrap',
  },
  itemDetail: {
    fontSize: '12px',
    color: '#713f12',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
    padding: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#475569',
  },
  input: {
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid #cbd5e1',
    fontSize: '13px',
    outline: 'none',
    transition: 'border-color 0.15s',
    width: '100%',
    boxSizing: 'border-box',
  },
  notesGroup: {
    padding: '0 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  textarea: {
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid #cbd5e1',
    fontSize: '13px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  },
  submitSection: {
    padding: '24px 32px 32px',
    textAlign: 'center',
  },
  submitButton: {
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 48px',
    fontSize: '15px',
    fontWeight: '600',
    transition: 'background-color 0.15s',
  },
  submitNote: {
    marginTop: '10px',
    fontSize: '12px',
    color: '#94a3b8',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#2563eb',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    color: '#64748b',
    fontSize: '14px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  errorIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: '700',
  },
  errorTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#1e293b',
  },
  errorText: {
    margin: 0,
    color: '#64748b',
    fontSize: '14px',
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  successIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: '#f0fdf4',
    color: '#16a34a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    fontWeight: '700',
    border: '2px solid #bbf7d0',
  },
  successTitle: {
    margin: 0,
    fontSize: '20px',
    color: '#1e293b',
  },
  successText: {
    margin: 0,
    color: '#64748b',
    fontSize: '14px',
    lineHeight: '1.5',
    maxWidth: '400px',
  },
};

export default VendorQuoteForm;
